import styled from '@emotion/styled';
import { components as coreComponents } from '@tempi/core-elements/dist/renderer';
import {
  BuilderComponent,
  Device,
  BuilderConfig,
  buildComponentDict,
  WebsiteMenu,
} from '@tempi/core-renderer';
import { components as ecomComponents } from '@tempi/ecom-elements/dist/renderer';
import { useRouter } from 'next/router';
import { getEnv } from 'helpers';
import { useTenantConfig, useTerraScreenConfig } from 'hooks';
import { useMemo } from 'react';
import { usePbLayout } from './hooks';
import { mappingDynamicDesktopSlots, mappingDynamicMobileSlots } from './slots';
import {
  ElementInterface,
  ExtraStoreState,
  LayoutConfig,
  DynamicSlotComponents,
} from './types';
import dynamic from 'next/dynamic';
import { ScreenConfig } from '@terra-js/shop-front-kit';

export interface PbLayoutProps {
  children: React.ReactNode;
  config: LayoutConfig;
  pageProps: MyAppProps['pageProps'] & {
    menu?: WebsiteMenu[];
  };
}

const BackButtonHeaderMobile = dynamic(() =>
  import('./slots/BackButtonHeaderMobile').then(
    (f) => f.BackButtonHeaderMobile,
  ),
);

export const PbLayout: React.FC<PbLayoutProps> = ({
  config: serverConfig,
  children,
  pageProps,
}) => {
  const { isMobile, terminal, location, terminalId } = useTenantConfig();
  const device: Device = isMobile ? 'mobile' : 'desktop';
  const router = useRouter();
  const { query } = router;
  const page = usePbLayout({
    device,
    defaultValue: {
      layoutConfig: {
        ...serverConfig,
      },
      menu: pageProps?.menu || [],
    },
  });

  const { layoutConfig, menu, websiteId } = page;

  const { partner, getProcessEnv } = useTenantConfig();
  const screenConfig: ScreenConfig | undefined = useTerraScreenConfig();
  const backappUrl = getProcessEnv('BACKAPP_URL');

  const showBackButton =
    !screenConfig?.header?.isBackIconHidden && (!!partner || !!backappUrl);

  const mappingDynamicMobileSlotsAddNew: DynamicSlotComponents = {
    ...mappingDynamicMobileSlots,
    ...(showBackButton
      ? {
          back_button_header_mobile: BackButtonHeaderMobile,
        }
      : {}),
  };

  const mappingDynamicSlots =
    device === 'mobile'
      ? mappingDynamicMobileSlotsAddNew
      : mappingDynamicDesktopSlots;

  const componentDict = useMemo(
    () =>
      buildComponentDict({
        ...coreComponents,
        ...ecomComponents,
      }),
    [],
  );

  return (
    <Wrapper id="tempi-layout">
      {layoutConfig?.header && (!query.hideHeader || !query.supportOmniAppUI) && (
        <BuilderComponent<ElementInterface, ExtraStoreState>
          componentDict={componentDict}
          content={layoutConfig.header}
          configs={{
            device,
            env: getEnv('LANDING_BUILDER_ENV') as BuilderConfig['env'],
            terminalCode: terminal,
            terminalId,
            locationCode: location,
            mappingDynamicSlots,
            globalData: { menu, websiteId },
          }}
          rootId="HEADER"
        />
      )}
      {children}
      {layoutConfig?.footer && (
        <BuilderComponent<ElementInterface, ExtraStoreState>
          componentDict={componentDict}
          content={layoutConfig.footer}
          configs={{
            device,
            env: getEnv('LANDING_BUILDER_ENV') as BuilderConfig['env'],
            terminalCode: terminal,
            terminalId: terminalId,
            locationCode: location,
            mappingDynamicSlots,
            globalData: { menu, websiteId },
          }}
          rootId="FOOTER"
          addGlobalStyle={!layoutConfig?.header}
        />
      )}
    </Wrapper>
  );
};

const Wrapper = styled.div`
  line-height: 1.5;
`;
